import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const category = categoryMap[article.category];

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg h-[400px] cursor-pointer"
      >
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full mb-3 whitespace-nowrap">
            {category?.name || article.category}
          </span>
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2 line-clamp-2 font-heading">
            {article.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-white/50 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount}
            </span>
          </div>
        </div>
        {article.hot && (
          <div className="absolute top-4 right-4 bg-accent-500 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap">
            HOT
          </div>
        )}
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 font-heading">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-white rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative overflow-hidden h-48">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 px-2 py-1 text-xs font-medium bg-primary-500 text-white rounded whitespace-nowrap">
          {category?.name || article.category}
        </span>
        {article.hot && (
          <span className="absolute top-3 right-3 bg-accent-500 text-white text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap">
            HOT
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-bold text-foreground-800 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors duration-200 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {article.commentCount}
          </span>
        </div>
      </div>
    </Link>
  );
}